/**
 * Hero « storyboard » de la landing (migration 0512). PUR, testé, partagé
 * app (admin) ⇄ marketing (affichage).
 *
 * Une PHASE = un fond (photo ou vidéo), un argumentaire, un appareil où joue le
 * ShowcasePlayer (téléphone ou ordinateur) et une durée. Les bornes Zod sont le
 * miroir des CHECK SQL de `landing_hero_phases` — garder synchro.
 * `DEFAULT_HERO_PHASES` est le miroir du seed de la migration (testé) et sert de
 * repli quand la base ne renvoie aucune phase publiée.
 */

import { z } from 'zod';

export const HERO_DURATION_MIN_MS = 3000;
export const HERO_DURATION_MAX_MS = 30000;
export const HERO_DURATION_DEFAULT_MS = 10000;
export const HERO_MAX_BULLETS = 3;

export const HERO_DEVICES = ['mobile', 'desktop'] as const;
export type HeroDevice = (typeof HERO_DEVICES)[number];

export const HERO_MEDIA_TYPES = ['image', 'video'] as const;
export type HeroMediaType = (typeof HERO_MEDIA_TYPES)[number];

/** Cadrages autorisés (valeur CSS `object-position`, liste blanche SQL). */
export const HERO_MEDIA_POSITIONS = [
  'center',
  'top',
  'center 25%',
  'center 35%',
  'center 45%',
  'center 65%',
  'bottom',
  'left',
  'right',
] as const;
export type HeroMediaPosition = (typeof HERO_MEDIA_POSITIONS)[number];

export const HERO_MEDIA_POSITION_LABELS: Record<HeroMediaPosition, string> = {
  center: 'Centre',
  top: 'Haut',
  'center 25%': 'Haut (25 %)',
  'center 35%': 'Un peu haut (35 %)',
  'center 45%': 'Presque centré (45 %)',
  'center 65%': 'Un peu bas (65 %)',
  bottom: 'Bas',
  left: 'Gauche',
  right: 'Droite',
};

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

/** Ce que l'admin saisit (sans id ni dates). */
export const heroPhaseInputSchema = z.object({
  display_order: z.number().int(),
  is_published: z.boolean(),
  eyebrow: z.string().trim().min(1).max(60),
  title: z.string().trim().min(1).max(120),
  title_highlight: optionalText(120),
  description: z.string().trim().min(1).max(400),
  bullets: z.array(z.string().trim().min(1).max(80)).max(HERO_MAX_BULLETS),
  caption_title: z.string().trim().min(1).max(60),
  caption_subtitle: optionalText(100),
  media_type: z.enum(HERO_MEDIA_TYPES),
  media_url: z.string().trim().url().max(1000),
  poster_url: z
    .string()
    .trim()
    .url()
    .max(1000)
    .nullish()
    .or(z.literal(''))
    .transform((v) => (v ? v : null)),
  media_position: z.enum(HERO_MEDIA_POSITIONS),
  device: z.enum(HERO_DEVICES),
  duration_ms: z.number().int().min(HERO_DURATION_MIN_MS).max(HERO_DURATION_MAX_MS),
});
export type HeroPhaseInput = z.input<typeof heroPhaseInputSchema>;

export const heroPhaseSchema = heroPhaseInputSchema.extend({ id: z.string().min(1) });
export type HeroPhase = z.output<typeof heroPhaseSchema>;

const UNSPLASH = (photo: string) =>
  `https://images.unsplash.com/${photo}?auto=format&fit=crop&w=1800&q=85`;

/** Miroir EXACT du seed de la migration 0512 (repli si la base est vide). */
export const DEFAULT_HERO_PHASES: HeroPhase[] = [
  {
    id: 'default-1',
    display_order: 10,
    is_published: true,
    eyebrow: 'LA RÉVISION QUI TE SUIT',
    title: 'Apprendre à raisonner.',
    title_highlight: 'Pas à recopier.',
    description:
      'Chaque exercice est découpé en étapes : à chaque étape, tu choisis la bonne stratégie et tu comprends pourquoi elle marche, au lieu de lire un corrigé tout fait.',
    bullets: [
      'Trouve la bonne méthode',
      'Comprends chaque étape',
      'Commence gratuitement, sans carte bancaire',
    ],
    caption_title: 'Dans sa chambre',
    caption_subtitle: 'Une dernière révision avant de dormir',
    media_type: 'image',
    media_url: UNSPLASH('photo-1516321318423-f06f85e504b3'),
    poster_url: null,
    media_position: 'center 45%',
    device: 'mobile',
    duration_ms: 10000,
  },
  {
    id: 'default-2',
    display_order: 20,
    is_published: true,
    eyebrow: 'SKILLCARDS',
    title: 'Chaque méthode devient',
    title_highlight: 'une carte à collectionner.',
    description:
      'Formules, théorèmes, méthodes : chaque outil du programme est une SKILLCARD. À chaque bonne réponse, ses HORA montent ; si tu ne révises plus, ils redescendent doucement.',
    bullets: [
      'Des cartes de la 6e à la Terminale',
      'Ta maîtrise de chaque carte en un coup d’œil',
      'Une créature qui évolue quand la carte est complète',
    ],
    caption_title: 'Dans le parc',
    caption_subtitle: 'Une micro-session entre deux activités',
    media_type: 'image',
    media_url: UNSPLASH('photo-1499209974431-9dddcece7f88'),
    poster_url: null,
    media_position: 'center',
    device: 'mobile',
    duration_ms: 10000,
  },
  {
    id: 'default-3',
    display_order: 30,
    is_published: true,
    eyebrow: 'AUTOMATISMES & LIVE SCHOOL',
    title: 'Dix minutes suffisent',
    title_highlight: 'pour garder le rythme.',
    description:
      'Des séries de 10 questions sans calculatrice, comme les automatismes du brevet et de l’épreuve anticipée de maths. Et chaque dimanche, un live par programme avec une SKILLCARD légendaire à gagner.',
    bullets: [
      'Une série gratuite chaque mois',
      'Un classement en direct le dimanche',
      'Les chapitres déjà vus en classe',
    ],
    caption_title: 'En route vers le lycée',
    caption_subtitle: 'La progression continue partout',
    media_type: 'image',
    media_url: UNSPLASH('photo-1503676260728-1c00da094a0b'),
    poster_url: null,
    media_position: 'center',
    device: 'mobile',
    duration_ms: 10000,
  },
  {
    id: 'default-4',
    display_order: 40,
    is_published: true,
    eyebrow: 'FEUILLE DE BROUILLON',
    title: 'Écris ton calcul.',
    title_highlight: 'Chaque ligne est vérifiée.',
    description:
      'Sur ordinateur comme sur téléphone, tu poses ton raisonnement ligne par ligne. Chaque ligne est vérifiée tout de suite : tu sais où tu t’es trompé avant de répondre.',
    bullets: [
      'Un coup de pouce quand tu bloques',
      'Des HORA pour chaque étape juste',
      'Ton brouillon reste dans ton historique',
    ],
    caption_title: 'Au bureau',
    caption_subtitle: 'Sur ordinateur, le temps d’approfondir',
    media_type: 'image',
    media_url:
      'https://news.unl.edu/sites/default/files/styles/wide/public/media/images/20210713-remotelearning-nt.jpg?itok=Eakw280F',
    poster_url: null,
    media_position: 'center',
    device: 'desktop',
    duration_ms: 10000,
  },
  {
    id: 'default-5',
    display_order: 50,
    is_published: true,
    eyebrow: 'DIAGNOSTIC & SUIVI',
    title: 'Sache où tu en es.',
    title_highlight: 'Progresse en confiance.',
    description:
      'Un diagnostic gratuit de ton programme montre tes points forts et ce qu’il reste à retravailler. Ton prof suit tes progrès et peut t’envoyer les bonnes SKILLCARDS.',
    bullets: [
      'Un diagnostic gratuit de 15 questions',
      'Un bilan clair, carte par carte',
      'Ton prof et tes parents voient tes progrès',
    ],
    caption_title: 'La satisfaction de comprendre',
    caption_subtitle: 'Une étape de plus, en confiance',
    media_type: 'image',
    media_url: UNSPLASH('photo-1516321318423-f06f85e504b3'),
    poster_url: null,
    media_position: 'center 35%',
    device: 'mobile',
    duration_ms: 10000,
  },
];

/**
 * Lignes lues en base → phases jouables : les lignes invalides et non publiées
 * sont écartées, l'ordre suit `display_order`. Liste vide → phases par défaut
 * (le Hero n'est jamais vide).
 */
export function normalizeHeroPhases(rows: readonly unknown[] | null | undefined): HeroPhase[] {
  const phases: HeroPhase[] = [];
  for (const row of rows ?? []) {
    const parsed = heroPhaseSchema.safeParse(row);
    if (parsed.success && parsed.data.is_published) phases.push(parsed.data);
  }
  phases.sort((a, b) => a.display_order - b.display_order);
  return phases.length > 0 ? phases : DEFAULT_HERO_PHASES;
}

/** Index suivant / précédent, en boucle. */
export function nextPhaseIndex(index: number, count: number, delta: number): number {
  if (count <= 0) return 0;
  return (((index + delta) % count) + count) % count;
}

/** « 01 / 05 ». */
export function phaseCounterLabel(index: number, count: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(index + 1)} / ${pad(count)}`;
}

/** Durée saisie en secondes (admin) → millisecondes bornées. */
export function heroDurationFromSeconds(seconds: number): number {
  if (!Number.isFinite(seconds)) return HERO_DURATION_DEFAULT_MS;
  const ms = Math.round(seconds * 1000);
  return Math.min(HERO_DURATION_MAX_MS, Math.max(HERO_DURATION_MIN_MS, ms));
}

/** Type de média deviné depuis une URL ou un nom de fichier. */
export function guessHeroMediaType(urlOrName: string): HeroMediaType {
  return /\.(mp4|webm|mov)(\?|#|$)/i.test(urlOrName) ? 'video' : 'image';
}

/** Ordre d'affichage d'une phase AJOUTÉE à la fin. */
export function nextHeroDisplayOrder(phases: readonly { display_order: number }[]): number {
  return phases.reduce((max, p) => Math.max(max, p.display_order), 0) + 10;
}

/**
 * Échange de place avec la voisine (↑ = -1, ↓ = +1). Renvoie les 2 mises à jour
 * d'ordre à écrire, ou `null` si la phase est déjà au bord.
 */
export function swapHeroPhaseOrder<T extends { id: string; display_order: number }>(
  phases: readonly T[],
  id: string,
  delta: -1 | 1,
): { id: string; display_order: number }[] | null {
  const sorted = [...phases].sort((a, b) => a.display_order - b.display_order);
  const i = sorted.findIndex((p) => p.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= sorted.length) return null;
  const a = sorted[i]!;
  const b = sorted[j]!;
  // Deux phases de même ordre : on recrée un écart pour que l'échange se voie.
  const aOrder = a.display_order === b.display_order ? b.display_order + delta : b.display_order;
  return [
    { id: a.id, display_order: aOrder },
    { id: b.id, display_order: a.display_order },
  ];
}

/** Bucket Storage public des fonds du Hero (migration 0512). */
export const HERO_MEDIA_BUCKET = 'hero-media';
export const HERO_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const HERO_VIDEO_MAX_BYTES = 30 * 1024 * 1024;
export const HERO_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const HERO_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'] as const;

/** Contrôle d'un fichier avant dépôt : message d'erreur lisible, ou null. */
export function heroMediaFileIssue(file: { type: string; size: number }): string | null {
  const isImage = (HERO_IMAGE_MIME_TYPES as readonly string[]).includes(file.type);
  const isVideo = (HERO_VIDEO_MIME_TYPES as readonly string[]).includes(file.type);
  if (!isImage && !isVideo) return 'Format refusé : JPEG, PNG, WebP, MP4 ou WebM.';
  if (isImage && file.size > HERO_IMAGE_MAX_BYTES) return 'Image trop lourde (5 Mo maximum).';
  if (isVideo && file.size > HERO_VIDEO_MAX_BYTES) return 'Vidéo trop lourde (30 Mo maximum).';
  return null;
}
